import { api } from '@/api';
import {
  getManageFlowBookingById,
  resetChangeTripsSelection,
} from '@/features/manage/manageActions';
import { buildExchangePassengerSpecifications } from '@/features/purchase/buildExchangePassengerSpecifications';
import { buildFindOffersPassengerSpecifications } from '@/features/purchase/buildFindOffersPassengerSpecifications';
import { buildUpdatePassengerPayload } from '@/features/purchase/buildUpdatePassengerPayload';
import { buildUpdatePurchaserPayload } from '@/features/purchase/buildUpdatePurchaserPayload';
import {
  createBooking,
  fetchBooking,
  fetchIssuer,
} from '@/features/purchase/purchaseService';
import { createAsyncThunk } from '@/store/utils';
import type { ApiErrorResponse, ErrorResponse } from '@/types/api';
import type {
  AddAncillariesBulkResponseDTO,
  AdditionalOfferPartDTO,
  BookingDTO,
  CreateBookingResponseDTO,
  FulfillmentDTO,
  NonTripAdmissionOfferPartDTO,
  PayWithAdyenBodyDTO,
  PayWithAdyenResponseDTO,
  PayWithExternalPaymentRequestBodyDTO,
  PayWithKlixBodyDTO,
  PayWithKlixResponseDTO,
  RemoveAncillaryParametersDTO,
  SearchJourneyOfferDTO,
  SearchJourneyOffersDTO,
  SearchNonTripOfferDTO,
  SearchNonTripOffersDTO,
  SearchOffersDTO,
  StartExchangeOperationResponseDTO,
} from '@/types/dto/agent';
import type {
  AdditionalOfferItem,
  AdditionalOffersCollection,
  OfferMapByLegId,
  OfferMapItem,
} from '@/types/offer';
import type { CheckoutPassenger } from '@/types/passenger';
import type {
  ExchangeOfferSearchRequestBody,
  NonTripOfferSearchRequestBodyWithSearchFormPassengers,
  OfferSearchRequestBodyWithSearchFormPassengers,
} from '@/types/purchase';
import type { TravelAccountDTO, TravelAccountsMap } from '@/types/travelpass';
import {
  getSelectedFulfillmentIds,
  getSelectedPassengers,
} from '@/utils/changeTrips';
import { getUniqueSelectedOffers } from '@/utils/purchase';
import { isNonEmptyString } from '@/utils/typeGuards';
import type { AncillaryValues, CheckoutValues } from '@/utils/zodSchema';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import { createAction } from '@reduxjs/toolkit';
import _partition from 'lodash/partition';

export const getOutboundJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getOutboundJourneys',
  async (payload, { dispatch }) => await dispatch(getJourneys(payload)).unwrap()
);

export const getInboundJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getInboundJourneys',
  async (payload, { dispatch }) => await dispatch(getJourneys(payload)).unwrap()
);

export const getJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getJourneys',
  async ({ passengers, promoCodes, ...rest }, { getState }) => {
    const {
      configuration: { salesPointConfiguration },
    } = getState();

    if (!salesPointConfiguration?.currencies?.length) {
      return null;
    }

    return (
      await api.agentApi.Offers_FindOffers(null, {
        ...rest,
        passengerSpecifications:
          buildFindOffersPassengerSpecifications(passengers),
        currency: salesPointConfiguration.currencies[0],
        promotionCodes: (promoCodes ?? []).map(({ code }) => ({
          code,
        })),
        corporateCodes: [],
      })
    ).data;
  }
);

// Change-booking variant of `getJourneys` using
// `Modifications_FindExchangeOffers`. Reads bookingId + fulfillmentIds from
// redux; `direction` picks outbound vs return fulfillments. Response is
// shape-compatible with `SearchOffersDTO`.
const getExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  ExchangeOfferSearchRequestBody & { direction: boolean }
>(
  'purchase/getExchangeOffers',
  async ({ promoCodes, direction, departureTime, ...rest }, { getState }) => {
    const state = getState();
    const bookingId = state.manage.booking?.id;
    const bookedTrips = state.manage.booking?.bookedTrips ?? [];
    const fulfillmentIds = getSelectedFulfillmentIds(
      state.manage.changeTrips,
      bookedTrips,
      direction
    );
    const passengerSpecifications = buildExchangePassengerSpecifications(
      getSelectedPassengers(
        state.manage.changeTrips,
        bookedTrips,
        state.manage.booking?.passengers ?? []
      )
    );
    const { salesPointConfiguration } = state.configuration;

    if (
      !bookingId ||
      !departureTime ||
      fulfillmentIds.length === 0 ||
      passengerSpecifications.length === 0 ||
      !salesPointConfiguration?.currencies?.length
    ) {
      return null;
    }

    const response = await api.agentApi.Modifications_FindExchangeOffers(
      { bookingId },
      {
        ...rest,
        departureTime,
        fulfillmentIds,
        passengerSpecifications,
        currency: salesPointConfiguration.currencies[0],
        promotionCodes: (promoCodes ?? []).map(({ code }) => ({ code })),
        corporateCodes: [],
      }
    );
    return response.data;
  }
);

export const getOutboundExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  ExchangeOfferSearchRequestBody
>(
  'purchase/getOutboundExchangeOffers',
  async (payload, { dispatch }) =>
    await dispatch(getExchangeOffers({ ...payload, direction: true })).unwrap()
);

export const getInboundExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  ExchangeOfferSearchRequestBody
>(
  'purchase/getInboundExchangeOffers',
  async (payload, { dispatch }) =>
    await dispatch(getExchangeOffers({ ...payload, direction: false })).unwrap()
);

const getUpgradeExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  { direction: boolean }
>('purchase/getUpgradeExchangeOffers', async ({ direction }, { getState }) => {
  const state = getState();
  const bookingId = state.manage.booking?.id;
  const bookedTrips = state.manage.booking?.bookedTrips ?? [];
  const fulfillmentIds = getSelectedFulfillmentIds(
    state.manage.changeTrips,
    bookedTrips,
    direction
  );
  const passengerRefs = getSelectedPassengers(
    state.manage.changeTrips,
    bookedTrips,
    state.manage.booking?.passengers ?? []
  )
    .map(({ externalReference }) => externalReference)
    .filter(isNonEmptyString);
  const { salesPointConfiguration } = state.configuration;

  if (
    !bookingId ||
    fulfillmentIds.length === 0 ||
    passengerRefs.length === 0 ||
    !salesPointConfiguration?.currencies?.length
  ) {
    return null;
  }

  const response = await api.agentApi.Modifications_FindUpgradeExchangeOffers(
    { bookingId },
    {
      fulfillmentIds,
      passengerRefs,
      currency: salesPointConfiguration.currencies[0],
      corporateCodes: [],
      promotionCodes: [],
    }
  );

  return response.data;
});

export const getOutboundUpgradeExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  void
>('purchase/getOutboundUpgradeExchangeOffers', async (_, { dispatch }) => {
  return await dispatch(getUpgradeExchangeOffers({ direction: true })).unwrap();
});

export const getInboundUpgradeExchangeOffers = createAsyncThunk<
  SearchOffersDTO | null,
  void
>('purchase/getInboundUpgradeExchangeOffers', async (_, { dispatch }) => {
  return await dispatch(
    getUpgradeExchangeOffers({ direction: false })
  ).unwrap();
});

// Fetches upgrade exchange offers for ALL selected fulfillments (both outbound
// and inbound) in a single request, then splits the response by journey
// direction. This ensures the BE treats all returned offers as belonging to
// the same exchange context — selecting one from each direction and committing
// them via POST /exchange-operations will succeed where two separate requests
// would produce "Selected exchange offers are not for the same fulfillments".
export const getAllUpgradeExchangeOffers = createAsyncThunk<
  { outbound: SearchOffersDTO; inbound: SearchOffersDTO },
  void
>('purchase/getAllUpgradeExchangeOffers', async (_, { getState }) => {
  const state = getState();
  const bookingId = state.manage.booking?.id;
  const bookedTrips = state.manage.booking?.bookedTrips ?? [];
  const { outboundTripId, returnTripId } = state.manage.changeTrips;
  const fulfillmentIds = getSelectedFulfillmentIds(
    state.manage.changeTrips,
    bookedTrips
  );
  const passengerRefs = getSelectedPassengers(
    state.manage.changeTrips,
    bookedTrips,
    state.manage.booking?.passengers ?? []
  )
    .map(({ externalReference }) => externalReference)
    .filter(isNonEmptyString);
  const { salesPointConfiguration } = state.configuration;

  if (
    !bookingId ||
    fulfillmentIds.length === 0 ||
    passengerRefs.length === 0 ||
    !salesPointConfiguration?.currencies?.length
  ) {
    return {
      outbound: { journeys: outboundTripId ? [] : undefined },
      inbound: { journeys: returnTripId ? [] : undefined },
    };
  }

  const response = await api.agentApi.Modifications_FindUpgradeExchangeOffers(
    { bookingId },
    {
      fulfillmentIds,
      passengerRefs,
      currency: salesPointConfiguration.currencies[0],
      corporateCodes: [],
      promotionCodes: [],
    }
  );

  const { journeys = [], ...rest } = response.data;
  return {
    outbound: {
      ...rest,
      journeys: outboundTripId
        ? journeys.filter((j) => j.isOutbound)
        : undefined,
    },
    inbound: {
      ...rest,
      journeys: returnTripId
        ? journeys.filter((j) => !j.isOutbound)
        : undefined,
    },
  };
});

// Rejects naturally on BE error so callers' `.unwrap().catch` toast pattern surfaces it.
export const getExchangePrice = createAsyncThunk<number | null, Array<string>>(
  'purchase/getExchangePrice',
  async (offerIds, { getState }) => {
    const bookingId = getState().manage.booking?.id;
    if (!bookingId || offerIds.length === 0) return null;
    const response = await api.agentApi.Modifications_CalculateExchangePrice(
      { bookingId },
      { offerIds }
    );
    return response.data.exchangePrice ?? null;
  }
);

// The authoritative "amount still to be paid" for the booking — used as the
// change-booking checkout's "Change total". Unlike the `exchangePrice` delta
// (offer-page preview), this is the BE's payable amount which nets out the
// reused credit from the original payment, change fees, vouchers, etc. Reads
// the booking id from the manage slice (the booking being modified). Empty
// `admissionIds` → whole-booking payable amount.
export const getPayableAmount = createAsyncThunk<number | null, void>(
  'purchase/getPayableAmount',
  async (_, { getState }) => {
    const bookingId = getState().manage.booking?.id;
    if (!bookingId) return null;
    const response = await api.agentApi.Bookings_GetBookingPayableAmount(
      { bookingId },
      { admissionIds: [] }
    );
    return response.data.payableAmount?.amount ?? null;
  }
);

// Commits the change-booking selection by creating a PREBOOKED exchange
// operation on the BE; it gets finalised when the customer pays on
// /purchase/checkout. Payload per `Modifications_StartExchangeOperation`:
// one entry per selected leg-offer with the offer id + passenger refs.
export const startExchange = createAsyncThunk<
  StartExchangeOperationResponseDTO,
  void
>('purchase/startExchange', async (_, { getState }) => {
  const state = getState();
  const bookingId = state.manage.booking?.id;
  if (!bookingId) {
    throw new Error('Cannot start exchange without a booking');
  }

  // Scope to passengers active on the selected trip(s) — sending all
  // booking passengers (including ones whose admission isn't on this
  // trip) makes the BE reject with "Some passengers don't match with
  // offers passengers". Agent-tool gets away with sending the user's
  // checkbox selection directly because their modify UI lets the user
  // pick passengers explicitly; we have no such picker and must derive
  // the right subset from the selected trip's active admissions.
  const passengerExternalReferences = getSelectedPassengers(
    state.manage.changeTrips,
    state.manage.booking?.bookedTrips ?? [],
    state.manage.booking?.passengers ?? []
  )
    .map(({ externalReference }) => externalReference)
    .filter(isNonEmptyString);

  const outboundOfferIds = getUniqueSelectedOffers(
    Object.values(state.purchase.outbound.selectedOfferMapByLegId)
  )
    .map(({ id }) => id)
    .filter(isNonEmptyString);
  const inboundOfferIds = getUniqueSelectedOffers(
    Object.values(state.purchase.inbound.selectedOfferMapByLegId)
  )
    .map(({ id }) => id)
    .filter(isNonEmptyString);

  const exchangeOffers = [...outboundOfferIds, ...inboundOfferIds].map(
    (id) => ({ id, passengerExternalReferences })
  );

  if (exchangeOffers.length === 0) {
    throw new Error('Cannot start exchange without selected offers');
  }

  const response = await api.agentApi.Modifications_StartExchangeOperation(
    { bookingId },
    { exchangeOffers }
  );
  return response.data;
});

// Journey-change deep-link commit. Same PREBOOKED exchange operation as
// `startExchange`, but driven by explicit offer ids + passenger refs, since the
// token flow resolves those itself and never populates the manage change-trips
// selection `startExchange` reads. It sets `exchangeOperation` through the same
// reducer cases, so the reused checkout and its abandon-on-exit cleanup work.
export const startJourneyChangeExchange = createAsyncThunk<
  StartExchangeOperationResponseDTO,
  {
    bookingId: string;
    offerIds: Array<string>;
    passengerExternalReferences: Array<string>;
  }
>(
  'purchase/startJourneyChangeExchange',
  async ({ bookingId, offerIds, passengerExternalReferences }) => {
    const exchangeOffers = offerIds.map((id) => ({
      id,
      passengerExternalReferences,
    }));
    const response = await api.agentApi.Modifications_StartExchangeOperation(
      { bookingId },
      { exchangeOffers }
    );
    return response.data;
  }
);

// Cleans up a pending exchange operation when the user cancels mid-flow.
// Silently ignores rejection (matches agent-tool): already-confirmed ops
// can't be deleted, so the cancel becomes a no-op from the customer's POV.
export const cancelExchange = createAsyncThunk<void, string>(
  'purchase/cancelExchange',
  async (exchangeOperationId, { getState }) => {
    const bookingId = getState().manage.booking?.id;
    if (!bookingId) return;
    try {
      await api.agentApi.Modifications_CancelExchangeOperation({
        bookingId,
        exchangeOperationId,
      });
    } catch {
      /* op already confirmed / gone — nothing to clean up */
    }
  }
);

export const getNonTripOffers = createAsyncThunk<
  SearchNonTripOffersDTO | null,
  Omit<
    NonTripOfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getNonTripOffers',
  async ({ passengers, promoCodes, ...rest }, { getState }) => {
    const {
      configuration: { salesPointConfiguration },
    } = getState();

    if (!salesPointConfiguration?.currencies?.length) {
      return null;
    }

    try {
      return (
        await api.agentApi.Offers_FindNonTripOffers(null, {
          ...rest,
          passengerSpecifications:
            buildFindOffersPassengerSpecifications(passengers),
          currency: salesPointConfiguration.currencies[0],
          promotionCodes: (promoCodes ?? []).map(({ code }) => ({
            code,
          })),
          corporateCodes: [],
        })
      ).data;
    } catch {
      return { nonTripOffers: [] };
    }
  }
);

export const updateSelectedNonTripOffer = createAction<SearchNonTripOfferDTO>(
  'purchase/updateSelectedNonTripOffer'
);

export const setSelectedOutboundJourney = createAction<SearchJourneyOffersDTO>(
  'purchase/setSelectedOutboundJourney'
);

export const setSelectedInboundJourney = createAction<SearchJourneyOffersDTO>(
  'purchase/setSelectedInboundJourney'
);

export const setSelectedOutboundOfferMapByLegId = createAction<OfferMapByLegId>(
  'purchase/setSelectedOutboundOfferMapByLegId'
);

export const updateSelectedOutboundOfferMapByLegId = createAction<OfferMapItem>(
  'purchase/updateSelectedOutboundOfferMapLegId'
);

export const setSelectedInboundOfferMapByLegId = createAction<OfferMapByLegId>(
  'purchase/setSelectedInboundOfferMapByLegId'
);

export const updateSelectedInboundOfferMapByLegId = createAction<OfferMapItem>(
  'purchase/updateSelectedInboundOfferMapLegId'
);

export const resetPurchase = createAction('purchase/resetPurchase');

// Abandon an in-flight change-booking: release the BE exchange operation
// (the PREBOOKED reservation) and clear local exchange state. Fired when the
// user leaves checkout without paying via an uncontrolled exit (browser back
// / manual nav) — the explicit Cancel button and the expiry handler own their
// own teardown + navigation, and reset purchase before unmount, so this
// no-ops for them. The manage view refetches a clean booking on entry, so we
// deliberately don't refresh `manage.booking` here.
export const abandonExchange = createAsyncThunk<void, void>(
  'purchase/abandonExchange',
  async (_, { dispatch, getState }) => {
    const exchangeOperationId = getState().purchase.exchangeOperation?.id;
    if (exchangeOperationId) {
      // Await so the DELETE completes before we drop local state, but don't
      // `.unwrap()`: the local reset must run regardless of the cancel result.
      await dispatch(cancelExchange(exchangeOperationId));
    }
    dispatch(resetPurchase());
    dispatch(resetChangeTripsSelection());
  }
);

export const getPurchaseFlowBookingAdditionalOffersSearch = createAsyncThunk<
  {
    outboundAdditionalOffers: Array<AdditionalOffersCollection>;
    inboundAdditionalOffers: Array<AdditionalOffersCollection>;
  },
  string
>(
  'purchase/getPurchaseFlowBookingAdditionalOffersSearch',
  async (bookingId, { getState }) => {
    const booking = getState().purchase.booking;

    const [outboundBookedTrips, inboundBookedTrips] = _partition(
      booking?.bookedTrips,
      'isOutbound'
    );

    const outboundOfferIds =
      outboundBookedTrips
        .flatMap((bookedTrip) =>
          bookedTrip.bookedOffers?.map((bookedOffer) => bookedOffer.id)
        )
        .filter(isNonEmptyString) || [];

    const inboundOfferIds =
      inboundBookedTrips
        .flatMap((bookedTrip) =>
          bookedTrip.bookedOffers?.map((bookedOffer) => bookedOffer.id)
        )
        .filter(isNonEmptyString) || [];

    const responseItems = (
      await api.agentApi.BookedOffers_GetAdditionalOffersSearch(
        {
          bookingId,
        },
        {
          bookedOfferIds: [...outboundOfferIds, ...inboundOfferIds],
        }
      )
    ).data.items;

    const mergeOfferParts = ({
      existingParts,
      newParts,
      bookedOfferId,
      additionalOfferId,
    }: {
      existingParts?: Array<AdditionalOfferItem>;
      newParts?: Array<AdditionalOfferPartDTO>;
      bookedOfferId: string;
      additionalOfferId?: string;
    }): Array<AdditionalOfferItem> =>
      additionalOfferId
        ? [
            ...(existingParts || []),
            ...(newParts || []).map((offer) => ({
              ...offer,
              bookedOfferId,
              additionalOfferId,
            })),
          ]
        : [];

    const additionalOfferCollectionByOfferId =
      responseItems?.reduce<Record<string, AdditionalOffersCollection>>(
        (
          acc,
          {
            bookedOfferId,
            additionalOfferId,
            ancillaryOfferParts,
            reservationOfferParts,
            admissionOfferParts,
          }
        ) => {
          return !bookedOfferId
            ? acc
            : {
                ...acc,
                [bookedOfferId]: {
                  ancillaryOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.ancillaryOfferParts,
                    newParts: ancillaryOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                  reservationOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.reservationOfferParts,
                    newParts: reservationOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                  admissionOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.admissionOfferParts,
                    newParts: admissionOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                },
              };
        },
        {}
      ) || {};

    return {
      outboundAdditionalOffers: outboundOfferIds
        .map((id) => additionalOfferCollectionByOfferId[id])
        .filter(Boolean),
      inboundAdditionalOffers: inboundOfferIds
        .map((id) => additionalOfferCollectionByOfferId[id])
        .filter(Boolean),
    };
  }
);

export const updateBookingPassengers = createAsyncThunk<
  void,
  { bookingId: string; passengers: Array<CheckoutPassenger> }
>('purchase/updateBookingPassengers', async ({ bookingId, passengers }) => {
  for (const { id: passengerId, ...rest } of passengers) {
    await api.agentApi.Bookings_UpdatePassenger(
      { bookingId, passengerId },
      buildUpdatePassengerPayload(rest)
    );
  }
});

export const updatePurchaseFlowBookingPurchaser = createAsyncThunk<
  object,
  CheckoutValues['purchaser']
>(
  'purchase/updatePurchaseFlowBookingPurchaser',
  async (purchaser, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    return await api.agentApi.Bookings_UpdatePurchaser(
      { bookingId },
      buildUpdatePurchaserPayload(purchaser)
    );
  }
);

export const addPurchaseFlowBookingAncillaries = createAsyncThunk<
  AddAncillariesBulkResponseDTO | ErrorResponse,
  AncillaryValues['selectedAncillaries']
>(
  'purchase/addPurchaseFlowBookingAncillaries',
  async (values, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    const bookedOfferId = values[0].bookedOfferId;

    return await api.agentApi
      .BookedOffers_AddAncillariesBulk(
        { bookingId, bookedOfferId },
        {
          ancillaries: values.map((selectedAncillary) => ({
            ancillaryId: selectedAncillary.id,
            offerId: selectedAncillary.additionalOfferId,
            passengerRefs: selectedAncillary.passengersExternalReferences,
          })),
        }
      )
      .catch((res: ApiErrorResponse) => res.response!.data);
  }
);

export const removePurchaseFlowBookingAncillary = createAsyncThunk<
  object,
  Omit<RemoveAncillaryParametersDTO, 'bookingId'>
>(
  'purchase/removePurchaseFlowBookingAncillary',
  async (pathParams, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    return (
      await api.agentApi.BookedOffers_DeleteAncillary({
        bookingId,
        ...pathParams,
      })
    ).data;
  }
);

const preparePromoCodes = (
  offers:
    | Array<SearchJourneyOfferDTO>
    | Array<NonTripAdmissionOfferPartDTO> = []
) =>
  Object.entries(
    offers.reduce<Record<string, string | undefined>>((acc, offer) => {
      (offer.appliedReductions ?? []).forEach(({ code, issuer }) => {
        if (code && !acc[code]) {
          acc[code] = issuer;
        }
      });

      return acc;
    }, {})
  ).map(([code, issuer]) => ({ code, issuer }));

export const createPurchaseFlowTripOfferBooking = createAsyncThunk<
  CreateBookingResponseDTO,
  SearchFormValues['passengers']
>(
  'purchase/createPurchaseFlowTripOfferBooking',
  async (passengers, { getState }) => {
    const { outbound, inbound } = getState().purchase;
    const passengerExternalReferences = passengers.map(
      ({ externalReference }) => externalReference
    );

    const selectedOffers = getUniqueSelectedOffers([
      ...Object.values(outbound.selectedOfferMapByLegId),
      ...Object.values(inbound.selectedOfferMapByLegId),
    ]).filter(
      (
        offer
      ): offer is Omit<SearchJourneyOfferDTO, 'id'> & {
        id: NonNullable<SearchJourneyOfferDTO['id']>;
      } => !!offer.id
    );
    const offers = selectedOffers.map(({ id }) => ({
      id,
      passengerExternalReferences,
    }));
    const promotionCodes = preparePromoCodes(selectedOffers);

    return createBooking(offers, passengers, promotionCodes);
  }
);

export const createPurchaseFlowNonTripOfferBooking = createAsyncThunk<
  CreateBookingResponseDTO,
  SearchFormValues['passengers']
>(
  'purchase/createPurchaseFlowNonTripOfferBooking',
  async (passengers, { getState }) => {
    const { nonTripOffers } = getState().purchase;
    const passengerExternalReferences = passengers.map(
      ({ externalReference }) => externalReference
    );
    const offers = nonTripOffers.selectedNonTripOffer?.id
      ? [
          {
            id: nonTripOffers.selectedNonTripOffer?.id,
            passengerExternalReferences,
          },
        ]
      : [];

    const promotionCodes = preparePromoCodes(
      nonTripOffers.selectedNonTripOffer?.admissionOfferParts
    );

    return createBooking(offers, passengers, promotionCodes);
  }
);

export const getPurchaseFlowBookingById = createAsyncThunk<
  BookingDTO,
  string | null | undefined
>('purchase/getPurchaseFlowBookingById', fetchBooking);

// Separate action to fetch booking while on seat selection modal to avoid duplicate loading spinners
export const getSeatSelectionBookingById = createAsyncThunk<
  BookingDTO,
  string | null | undefined
>('purchase/getSeatSelectionBookingById', fetchBooking);

// Refetches the checkout booking into whichever slice the current flow
// reads from. Seat/ancillary mutations hit the same booking id regardless
// of flow, but the refreshed result has to land where the UI reads it:
//   - change-booking → manage.booking (what `useCheckoutBooking` returns)
//   - regular        → purchase.booking, preserving the seat-modal's
//                       quiet refetch (`getSeatSelectionBookingById`) so
//                       it doesn't trigger the main checkout spinner.
export const refreshCheckoutBooking = createAsyncThunk<
  BookingDTO | null,
  { quiet?: boolean } | undefined
>('purchase/refreshCheckoutBooking', async (arg, { getState, dispatch }) => {
  const state = getState();
  const { outboundTripId, returnTripId } = state.manage.changeTrips;
  const isChangeBooking = Boolean(outboundTripId || returnTripId);

  if (isChangeBooking) {
    const id = state.manage.booking?.id;
    if (!id) return null;
    return await dispatch(getManageFlowBookingById(id)).unwrap();
  }

  const id = state.purchase.booking?.id;
  if (!id) return null;
  return await dispatch(
    arg?.quiet
      ? getSeatSelectionBookingById(id)
      : getPurchaseFlowBookingById(id)
  ).unwrap();
});

export const deletePurchaseFlowBooking = createAsyncThunk<
  object,
  NonNullable<BookingDTO['id']>
>('purchase/deletePurchaseFlowBooking', async (bookingId) => {
  return await api.agentApi.Bookings_DeleteBooking({ bookingId });
});

export const getPurchaseFlowTravelAccountsMap = createAsyncThunk<
  TravelAccountsMap,
  Array<FulfillmentDTO>
>('purchase/getPurchaseFlowTravelAccountsMap', async (fulfillments) => {
  const isValidFulfillment = (
    fulfillment: FulfillmentDTO
  ): fulfillment is { controlNumber: string; issuer: string } =>
    Boolean(fulfillment.controlNumber) && Boolean(fulfillment.issuer);

  const travelAccounts = await Promise.all(
    fulfillments.filter(isValidFulfillment).map((fulfillment) =>
      fetchIssuer({
        controlNumber: fulfillment.controlNumber,
        issuer: fulfillment.issuer,
      })
    )
  );

  return travelAccounts.reduce<
    Record<
      `${NonNullable<TravelAccountDTO>['issuer']}-${NonNullable<TravelAccountDTO['number']>}`,
      TravelAccountDTO
    >
  >((acc, { travelAccount }) => {
    if (travelAccount?.issuer && travelAccount.number) {
      acc[`${travelAccount.issuer}-${travelAccount.number}`] = travelAccount;
    }

    return acc;
  }, {});
});

export const initiateAdyenPayment = createAsyncThunk<
  PayWithAdyenResponseDTO,
  { bookingId: string } & PayWithAdyenBodyDTO
>('purchase/initiateAdyenPayment', async ({ bookingId, ...body }) => {
  return (await api.agentApi.Payments_PayWithAdyenCheckout({ bookingId }, body))
    .data;
});

export const initiateKlixPayment = createAsyncThunk<
  PayWithKlixResponseDTO,
  { bookingId: string } & PayWithKlixBodyDTO
>('purchase/initiateKlixPayment', async ({ bookingId, ...body }) => {
  return (await api.agentApi.Payments_PayWithKlix({ bookingId }, body)).data;
});

export const initiateExternalPayment = createAsyncThunk<
  unknown,
  { bookingId: string } & PayWithExternalPaymentRequestBodyDTO
>('purchase/initiateExternalPayment', async ({ bookingId, ...body }) => {
  return await api.agentApi.Payments_PayWithExternalPayment(
    { bookingId },
    body
  );
});

export const initiateZeroPayment = createAsyncThunk<unknown, string>(
  'purchase/initiateZeroPayment',
  async (bookingId) => {
    return (
      await api.agentApi.Payments_PayWithZeroPayment(
        { bookingId },
        { offerPartIds: [] }
      )
    ).data;
  }
);
